// ©2024 Austin App House. All rights reserved.
import React from 'react';
import { MDBIcon } from 'mdbreact';
import DashboardIcon from '../icons/DashboardIcon';
import ControlsButton from '../ControlsButton';
import workOrderStatusControl from '../../../utils/workOrderStatusControl';
import { localCurrencySettings, variableTypeShorts } from '../../../utils/constans';
import EstimatedTimeOverageLabel from '../../layout/EstimatedTimeOverageLabel';
import formatAMPM from '../../../utils/formatAMPM';
import { getDateWithOffset } from '../../../utils/getDateWithOffset';
import getQtyByPayType from '../../../utils/getQtyByPayType';
import { useMemo } from 'react';
import { calculateFundsRequiredSum } from '../../../utils/calculateFundsRequiredSum';

//

const toggleCheckbox = (workOrder, selected, select) => {
  if (workOrder.status === 'completed');
  const id = workOrder._id;
  if (selected.includes(id)) {
    return select(selected.filter((el) => el !== id));
  }
  return select([...selected, id]);
};

const isLessThanHalfDay = (startDate) => ((new Date(startDate) - Date.now())) < 12 * 60 * 60 * 1000;

const WorkOrderCard = ({
  workOrder,
  activeTab,
  selectedWorkOrders,
  userType,
  setCurrentWorkOrder,
  fetchApplicantsForWorkOrder,
  toggleModal,
  messagesCount,
  selectWorkOrder,
  publishWorkOrder,
  acceptInvite,
  confirmWorkOrder,
  createWorkOrderTemplateByWorkOrder,
}) => {
  const workOrderControlByStatus = workOrderStatusControl(workOrder.status);
  const finalEstimateTotal = useMemo(() => {
    const basicQty = getQtyByPayType(
      workOrder.basicType,
      workOrder.approvedHoursQty || workOrder.quantity,
      workOrder.approvedDeviceQty || workOrder.maxQuantity,
      workOrder.quantity,
      workOrder.payBaseRateAsMinimum,
    );
    let approvedBasePotentialPay = (workOrder.bidAmountBase || workOrder.amount) * basicQty;
    if (parseFloat(workOrder.bidAmountVariable) || parseFloat(workOrder.variableAmount)) {
      const actualVariableQty = getQtyByPayType(
        workOrder.variableType,
        workOrder.approvedHoursQty || workOrder.quantity,
        workOrder.approvedDeviceQty || workOrder.maxQuantity,
        Infinity,
      );

      if (actualVariableQty > parseFloat(workOrder.variablePayAfter)) {
        const diffOfBaseTotalHrs = Math.min(
          parseFloat(actualVariableQty) - parseFloat(workOrder.variablePayAfter),
          parseFloat(workOrder.maxQuantity) - parseFloat(workOrder.variablePayAfter),
        );
        const variablePay = diffOfBaseTotalHrs * (workOrder.bidAmountVariable || workOrder.variableAmount);
        approvedBasePotentialPay += variablePay;
      }
    }
    return approvedBasePotentialPay;
  }, [workOrder])

  const totalEstPaySum = useMemo(() => {
    const sum = calculateFundsRequiredSum({
      amount: workOrder.bidAmountBase || workOrder.amount,
      basicType: workOrder.basicType,
      quantity: workOrder.quantity,
      variableAmount: workOrder.variableAmount,
      maxQuantity: workOrder.maxQuantity,
      variablePayAfter: workOrder.variablePayAfter
    }).toLocaleString('en-US', localCurrencySettings);

    const sumNum = Number(sum.slice(1));
    if (
      workOrder.payBaseRateAsMinimum && sumNum < workOrder.amount) {
      return workOrder.potentialPay
    }
    return sum;
  }, [workOrder])

  const basePaySum = useMemo(() => {
    let hrs = workOrder.approvedHoursQty || workOrder.quantity;
    let pay;
    if (workOrder.approvedHoursQty > workOrder.quantity) {
      hrs = workOrder.quantity
    }
    if (workOrder.basicType === 'Site') {
      hrs = 1
    }
    if (workOrder.basicType === 'Device') {
      hrs = (workOrder.approvedDeviceQty || workOrder.qtyDevices) < workOrder.quantity ? workOrder.approvedDeviceQty || workOrder.qtyDevices : workOrder.quantity
    }
    if (workOrder.approvedHoursQty < workOrder.quantity && !workOrder.payBaseRateAsMinimum) {
      pay = (workOrder.bidAmountBase || workOrder.amount) * hrs;
    } else if (workOrder.payBaseRateAsMinimum && workOrder.approvedHoursQty < workOrder.quantity) {
      pay = (workOrder.bidAmountBase || workOrder.amount) * workOrder.quantity;
    } else {
      pay = (workOrder.bidAmountBase || workOrder.amount) * hrs;
    }
    return pay;
  }, [workOrder])

  const basePay = useMemo(() => {
    let hrs = workOrder.approvedHoursQty || workOrder.quantity;
    if (workOrder.approvedHoursQty > workOrder.quantity) {
      hrs = workOrder.quantity
    }

    if (workOrder.basicType === 'Device') {
      hrs += ' device(s)'
    } else {
      hrs += ' hr(s)'
    }
    return `${workOrder.bidAmountBase || workOrder.amount}/${variableTypeShorts[workOrder.basicType]} for ${hrs} = ${basePaySum}`
  }, [workOrder]);

  const variablePaySum = useMemo(() => {

    return ((workOrder.bidAmountVariable || workOrder.variableAmount) * (workOrder.maxQuantity ? workOrder.maxQuantity - workOrder.variablePayAfter : 0)) || 0
  }, [workOrder])

  const varPay = useMemo(() => {
    if (workOrder.variableType === 'Hour') {
      return `
        ${workOrder.bidAmountVariable || workOrder.variableAmount}/hr after ${workOrder.variablePayAfter} for max ${workOrder.maxQuantity} hrs = ${variablePaySum}
      `
    }
    if (workOrder.variableType === 'Device') {
      return `
        ${workOrder.bidAmountVariable || workOrder.variableAmount}/dev after ${workOrder.variablePayAfter} for max ${workOrder.maxQuantity} devices = ${variablePaySum}
      `
    }
  }, [workOrder, variablePaySum])

  // getApprovedExpensesAmount = () => {
  //   const { selectedExpenses } = this.state;
  //   return selectedExpenses.reduce((acc, cur) => acc + cur.amount, 0);
  // }
  // this.setState({ finalEstimateTotal: approvedBasePotentialPay });

  const isClient = userType === 'client';
  const showCardCheckbox = userType === 'contractor'
    ? ((!workOrderControlByStatus.beforeAssigned || activeTab === 'routed')
      && !workOrderControlByStatus.canceled)
    : true;
  const workOrderId = String(workOrder._id);
  const messageStats = messagesCount?.unReadMessages?.[workOrderId];
  const allMessagesCount = messageStats?.all ?? 0;
  const unreadMessagesCount = messageStats?.unread ?? 0;

  const clientWoStatus = isClient ? (() => {
    if (workOrder.onHold) return { modifier: 'draft', label: 'ON HOLD' };
    if (workOrderControlByStatus.draft) return { modifier: 'draft', label: 'DRAFT' };
    if (workOrder.status === 'assigned') {
      return workOrder.confirmed
        ? { modifier: 'confirmed', label: 'CONFIRMED' }
        : { modifier: 'unconfirmed', label: 'UNCONFIRMED' };
    }
    return null;
  })() : null;

  return (
    <article className={`work-order-card${workOrderControlByStatus.draft ? ' wo-card--draft' : ''}`}>
      <div className="wo-main">
        <div className="wo-info">
          <button type="button" className="wo-flag" aria-label="Flag as priority" aria-pressed="false">
            <DashboardIcon name="flag" size={16} strokeWidth="2" />
            <span className="wo-flag-label">Flag priority</span>
          </button>
          <h3 className="wo-title">{workOrder.title}</h3>
          <div className="wo-meta">
            <p>
              <span className="wo-label">
                {userType === 'contractor' ? 'Client:' : 'Created By:'}
              </span>
              {' '}
              {userType === 'contractor'
                ? workOrder.companyNCR?.name
                : workOrder.clientInfo?.name || workOrder.companyNCR?.name}
              {userType !== 'contractor' ? (
                <span className="dashdiv rating_value">{` ${workOrder.clientInfo?.ratings || 100}%`}</span>
              ) : (
                <span className="dashdiv rating_value">{` ${workOrder.clientInfo?.ratings || 100}%`}</span>
              )}
            </p>
            <p>
              <span className="wo-label">WIN:</span> {workOrder.WIN}
            </p>
            <p>
              <span className="wo-label">Company WO ID:</span> {workOrder.customTagId}
            </p>
            {workOrder.status === 'deleted' ? (
              <p className="wo-status-note wo-status-note--danger">Deleted WO</p>
            ) : null}
            {workOrder.status === 'canceled' ? (
              <p className="wo-status-note wo-status-note--warning">Canceled WO</p>
            ) : null}
            {isClient && workOrder?.templateId ? (
              <p>
                <span className="wo-label">Template Used</span>
              </p>
            ) : null}
            {!isClient && userType !== 'contractor' && workOrder?.templateId ? (
              <p className="wo-template-used">Template Used</p>
            ) : null}
          </div>
          <div className="wo-footer-meta">
            <button
              type="button"
              className={userType === 'contractor' ? 'wo-messages-link' : 'wo-messages'}
              onClick={() => {
                fetchApplicantsForWorkOrder(workOrder._id);
                toggleModal(true, 'workOrderMessageSystemModal', true, { workOrder });
              }}
            >
              {messagesCount ? (
                <span className="wo-messages-count">
                  <span>
                    {allMessagesCount === 1
                      ? '1 message'
                      : `${allMessagesCount} messages`}
                  </span>
                  {unreadMessagesCount ? (
                    <sup className="unread_badge">{unreadMessagesCount}</sup>
                  ) : null}
                </span>
              ) : ' messages'}
            </button>
            <span className="wo-location">
              {workOrder.city ? `${workOrder.city}` : ''}
              {workOrder?.city && workOrder?.state?.label ? ', ' : ''}
              {workOrder?.state?.label ? `${workOrder.state.label}` : ''}
              {workOrder.zipCode ? ` ${workOrder.zipCode}` : ''}
            </span>
            {!isClient && workOrder.distance ? (
              <span className="wo-distance">
                {`${workOrder.distance.toFixed(2)} Miles`}
              </span>
            ) : null}
          </div>
        </div>

        <div className="wo-dates">
          <p>
            <span className="wo-label">Start:</span>
            {new Date(workOrder.startDate).toLocaleDateString('en-US', { timeZone: 'UTC' }) === 'Invalid Date'
              ? ''
              : (
                <>
                  {' '}
                  {new Date(workOrder.startDate).toLocaleDateString('en-US', { timeZone: 'UTC' })}
                  {workOrder.startTime ? ` ${workOrder.startTime}` : ''}
                </>
              )}
          </p>
          <p>
            <span className="wo-label">End:</span>
            {new Date(workOrder.endDate).toLocaleDateString('en-US', { timeZone: 'UTC' }) === 'Invalid Date'
              ? ''
              : (
                <>
                  {' '}
                  {new Date(workOrder.endDate).toLocaleDateString('en-US', { timeZone: 'UTC' })}
                  {workOrder.endTime ? ` ${workOrder.endTime}` : ''}
                </>
              )}
          </p>
          {userType === 'contractor' && workOrder.workTypes instanceof Array && workOrder.workTypes.length ? (
            <p className="wo-category">
              {workOrder.workTypes.map((tool) => tool.title).join(' / ')}
            </p>
          ) : null}
          {workOrderControlByStatus.assigned
          && !workOrder.checkInOutDetails.length
          && (workOrder.contractorStatus?.workStatus === 'onWay') ? (
            <p className="en_route">
              <MDBIcon icon="truck" flip="horizontal" style={{ marginRight: '0.5rem' }} />
              <span>En Route</span>
            </p>
          ) : null}
          {userType !== 'contractor' && !workOrderControlByStatus.beforeAssigned && workOrder.checkInOutDetails.length ? (
          <div className="last-check-in-out-info-container">
            <div>
              <label>
                Last Check In:
                {' '}
                {formatAMPM(
                  getDateWithOffset(
                    workOrder
                      .checkInOutDetails[workOrder.checkInOutDetails.length - 1]
                      .check_in_date,
                  ),
                )}
              </label>
            </div>
            {workOrder
              .checkInOutDetails[workOrder.checkInOutDetails.length - 1]
              .check_out_date ? (
              <div>
                <label>
                  Checked Out:
                  {' '}
                  {formatAMPM(
                    getDateWithOffset(
                      workOrder
                        .checkInOutDetails[workOrder.checkInOutDetails.length - 1]
                        .check_out_date,
                    ),
                  )}
                </label>
              </div>
            ) : null}
          </div>
        ) : null}
        </div>

        <div className="wo-assigned">
          {userType !== 'contractor' ? (
            workOrder.contractor_id ? (
              <>
                <h4 className="wo-assigned-title">Assigned to :</h4>
                <p>{workOrder.contractorInfo?.name || 'Assigned'}</p>
                {workOrder.contractorInfo?.phone ? (
                  <p>
                    <span className="wo-label">Phone:</span> {workOrder.contractorInfo.phone}
                  </p>
                ) : null}
                {workOrder.contractorInfo?.email ? (
                  <p>
                    <span className="wo-label">Email:</span> {workOrder.contractorInfo.email}
                  </p>
                ) : null}
              </>
            ) : isClient && !workOrderControlByStatus.beforeAssigned ? (
              <>
                <h4 className="wo-assigned-title">Assigned to :</h4>
                <p className="wo-unassigned">Not yet assigned</p>
              </>
            ) : (
              <>
                <h4 className="wo-assigned-title">{isClient ? 'Assigned to :' : 'Applicants / Routed'}</h4>
                <p>
                  <span className="wo-label">Applicants:</span> {workOrder.applicant_count || 0}
                </p>
                <p>
                  <span className="wo-label">Routed To:</span> {workOrder.routed_count || 0}
                </p>
              </>
            )
          ) : (workOrder.contractorInfo ? (
            <>
              <h4 className="wo-assigned-title">Assigned to :</h4>
              <p>{workOrder.contractorInfo?.name}</p>
              <p>
                <span className="wo-label">Phone:</span> {workOrder.contractorInfo?.phone}
              </p>
              <p>
                <span className="wo-label">Email:</span> {workOrder.contractorInfo?.email}
              </p>
            </>
          ) : null)}
        </div>

        <div className="wo-pricing">
          {isClient && clientWoStatus ? (
            <span className={`wo-status wo-status--${clientWoStatus.modifier}`}>
              {clientWoStatus.label}
            </span>
          ) : null}
          {!workOrderControlByStatus.workUncompleted && !workOrderControlByStatus.canceled ? (
            isClient ? (
              <>
                <div className="wo-price">
                  {parseFloat(
                    workOrderControlByStatus.completed
                      ? (parseFloat(workOrder.finalPayAdjustment || 0) + parseFloat(finalEstimateTotal))
                      : (workOrder.finalPayGoFromWallet || workOrder.finalPay),
                  ).toLocaleString('en-US', localCurrencySettings)}
                </div>
                <div className="wo-price-detail">
                  <span>{workOrderControlByStatus.approved ? 'Final Pay' : 'Estimated Final Pay'}</span>
                </div>
                <div className="wo-price-calc">
                  <div>Base: {basePay}</div>
                  {workOrder.bidAmountVariable ? <div>Var: {varPay}</div> : null}
                </div>
                <div className="wo-price-total">Total est value = {totalEstPaySum}</div>
                {workOrderControlByStatus.completed
                  && workOrder.basicType === 'Hour'
                  && (workOrder.approvedHoursQty > (
                    workOrder.quantity
                    + (workOrder.variableType === 'Hour' ? (workOrder.maxQuantity - workOrder.variablePayAfter) : 0)
                  )) ? (
                  <EstimatedTimeOverageLabel text="Over Est. Time!" />
                ) : null}
              </>
            ) : (
              <>
                <div className={`wo-price${userType === 'contractor' ? ' wo-price--paid' : ''}`}>
                  {parseFloat(
                    (workOrderControlByStatus.completed || userType === 'contractor')
                      ? (parseFloat(workOrder.finalPayAdjustment || 0) + parseFloat(finalEstimateTotal))
                      : (workOrder.finalPayGoFromWallet || workOrder.finalPay),
                  ).toLocaleString('en-US', localCurrencySettings)}
                </div>
                <div className="wo-price-detail">
                  <span>{workOrderControlByStatus.approved ? 'Final Pay' : 'Estimated Final Pay'}</span>
                </div>
                <div className="wo-price-calc">
                  <div>Base: {basePay}</div>
                  {workOrder.bidAmountVariable ? <div>Var: {varPay}</div> : null}
                </div>
                <div className="wo-price-total">Total est value = {totalEstPaySum}</div>
                {workOrderControlByStatus.completed && userType !== 'contractor'
                  && workOrder.basicType === 'Hour'
                  && (workOrder.approvedHoursQty > (
                    workOrder.quantity
                    + (workOrder.variableType === 'Hour' ? (workOrder.maxQuantity - workOrder.variablePayAfter) : 0)
                  )) ? (
                  <EstimatedTimeOverageLabel text="Over Est. Time!" />
                ) : null}
              </>
            )
          ) : (
            isClient ? (
              <>
                <div className="wo-price">
                  {(
                    (
                      workOrderControlByStatus.beforeAssigned && !workOrderControlByStatus.canceled
                        ? workOrder.amount
                        : (workOrder.bidAmountBase || workOrder.amount)
                    )
                    * ((workOrder.payBaseRateAsMinimum && workOrder.basicType !== 'Site') ? workOrder.quantity : 1)
                  ).toLocaleString('en-US', localCurrencySettings)}
                </div>
                <div className="wo-price-detail">
                  {workOrder.payBaseRateAsMinimum
                    ? 'MIN'
                    : workOrder.basicType && workOrder.basicType !== ''
                      ? `Per ${workOrder.basicType === 'Hours' ? 'Hour' : workOrder.basicType}`
                      : ''}
                </div>
                <div className="wo-price-calc">
                  <div>Base: {basePay}</div>
                  {(workOrder.bidAmountVariable || workOrder.variablePayAfter) && workOrder.maxQuantity ? <div>Var: {varPay}</div> : null}
                </div>
                <div className="wo-price-total">Total est value = {totalEstPaySum}</div>
              </>
            ) : (
              <>
                <div className={`wo-price${userType === 'contractor' ? ' wo-price--paid' : ''}`}>
                  {(
                    (
                      workOrderControlByStatus.beforeAssigned && !workOrderControlByStatus.canceled
                        ? workOrder.amount
                        : (workOrder.bidAmountBase || workOrder.amount)
                    )
                    * ((workOrder.payBaseRateAsMinimum && workOrder.basicType !== 'Site') ? workOrder.quantity : 1)
                  ).toLocaleString('en-US', localCurrencySettings)}
                </div>
                <div className="wo-price-detail">
                  {workOrder.payBaseRateAsMinimum
                    ? 'MIN'
                    : workOrder.basicType && workOrder.basicType !== ''
                      ? `Per ${workOrder.basicType === 'Hours' ? 'Hour' : workOrder.basicType}`
                      : ''}
                </div>
                <div className="wo-price-calc">
                  <div>Base: {basePay}</div>
                  {(workOrder.bidAmountVariable || workOrder.variablePayAfter) && workOrder.maxQuantity ? <div>Var: {varPay}</div> : null}
                </div>
                <div className="wo-price-total">Total est value = {totalEstPaySum}</div>
              </>
            )
          )}
          {!isClient && userType !== 'contractor' && workOrder.status === 'assigned' ? (
            workOrder.confirmed ? (
              <span className="wo-status wo-status--confirmed">CONFIRMED</span>
            ) : (
              <span className="wo-status wo-status--unconfirmed">UNCONFIRMED</span>
            )
          ) : null}
          {!isClient && workOrder.onHold ? (
            <span className="wo-status wo-status--draft">ON HOLD</span>
          ) : null}
        </div>
      </div>

      <div className={`wo-actions${showCardCheckbox ? ' wo-actions--with-check' : ''}`}>
        {showCardCheckbox ? (
          <div className="wo-card-checkbox">
            <label className="wo-check-btn check_btn">
              <input
                type="checkbox"
                id={userType === 'contractor'
                  ? `${workOrder.status}Checkbox${workOrder._id}`
                  : `checkbox4${workOrder._id}`}
                checked={selectedWorkOrders.includes(workOrder._id)}
                onChange={() => {
                  toggleCheckbox(workOrder, selectedWorkOrders, selectWorkOrder);
                }}
              />
            </label>
          </div>
        ) : null}
        <div className="wo-actions__buttons">
          <ControlsButton
            workOrder={workOrder}
            activeTab={activeTab}
            selectedWorkOrders={selectedWorkOrders}
            userType={userType}
            fetchApplicantsForWorkOrder={fetchApplicantsForWorkOrder}
            toggleModal={toggleModal}
            selectWorkOrder={selectWorkOrder}
            publishWorkOrder={publishWorkOrder}
            setCurrentWorkOrder={setCurrentWorkOrder}
            acceptInvite={acceptInvite}
            confirmWorkOrder={confirmWorkOrder}
            createWorkOrderTemplateByWorkOrder={createWorkOrderTemplateByWorkOrder}
          />
        </div>
      </div>
    </article>
  );
};

export default WorkOrderCard;
